/*!
 * TinyPoker 額外交收管理組件
 * 提供 club_extra_settlements 的新增、刪除和列表功能
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button,
  Table,
  Badge,
  Alert,
  Input,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import axios from "axios";
import { TinyBusy, TinyLoadingOverlay } from "components/tiny/TinyLoading";
import TinyWeekSelector from "../../components/tiny/TinyWeekSelector";

const API_BASE = "/api/tiny";

// 額外交收類型
const SETTLEMENT_TYPES = [
  { value: "fine", label: "罰款", color: "#f5365c", bgColor: "rgba(245, 54, 92, 0.1)" },
  { value: "compensation", label: "補償", color: "#fb6340", bgColor: "rgba(251, 99, 64, 0.1)" },
  { value: "reward", label: "獎勵", color: "#2dce89", bgColor: "rgba(45, 206, 137, 0.1)" },
  { value: "tax", label: "稅收", color: "#11cdef", bgColor: "rgba(17, 205, 239, 0.1)" },
  { value: "rebate", label: "返利", color: "#5e72e4", bgColor: "rgba(94, 114, 228, 0.1)" },
  { value: "other", label: "其他", color: "#8965e0", bgColor: "rgba(137, 101, 224, 0.1)" },
];

// 默認額外交收記錄
const DEFAULT_SETTLEMENT = {
  week: "",
  club_id: "",
  super_agent_id: "",
  super_agent_name: "",
  agent_id: "",
  agent_name: "",
  player_id: "",
  player_name: "",
  type: "other",
  amount: 0,
  description: "",
  reason: "",
};

// 計算下一週（週一到週日）
const getNextWeek = (weekStr) => {
  // weekStr 格式：20260119-20260125
  const startStr = weekStr.split('-')[0];
  const year = parseInt(startStr.substring(0, 4));
  const month = parseInt(startStr.substring(4, 6)) - 1;
  const day = parseInt(startStr.substring(6, 8));
  
  const startDate = new Date(year, month, day);
  
  // 加7天得到下週一
  const nextMonday = new Date(startDate);
  nextMonday.setDate(startDate.getDate() + 7);
  
  // 下週日
  const nextSunday = new Date(nextMonday);
  nextSunday.setDate(nextMonday.getDate() + 6);
  
  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}${m}${d}`;
  };
  
  return `${formatDate(nextMonday)}-${formatDate(nextSunday)}`;
};

function TinyCustomSettlements() {
  // State
  const [settlements, setSettlements] = useState([]);
  // 類型分佈預設收起 —— 它是「想知道再看」的細節，不該擋在主表前面
  const [showTypeBreakdown, setShowTypeBreakdown] = useState(false);
  const [clubs, setClubs] = useState([]);
  const [allWeeks, setAllWeeks] = useState([]); // 從 DB 載入的週期 + 下一週
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Filters
  const [selectedWeek, setSelectedWeek] = useState("");
  const [filterClub, setFilterClub] = useState("");
  const [filterType, setFilterType] = useState("");

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [newSettlement, setNewSettlement] = useState({ ...DEFAULT_SETTLEMENT });
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState(null);

  // 即時驗證
  const validationErrors = useMemo(() => {
    const errors = [];
    const sId = (newSettlement.super_agent_id || "").trim();
    const sName = (newSettlement.super_agent_name || "").trim();
    const aId = (newSettlement.agent_id || "").trim();
    const aName = (newSettlement.agent_name || "").trim();
    const pId = (newSettlement.player_id || "").trim();
    const pName = (newSettlement.player_name || "").trim();

    if ((!sId) !== (!sName)) errors.push("超級代理 ID 與名稱必須同時填寫或同時留空");
    if ((!aId) !== (!aName)) errors.push("代理 ID 與名稱必須同時填寫或同時留空");
    if ((!pId) !== (!pName)) errors.push("玩家 ID 與名稱必須同時填寫或同時留空");
    return errors;
  }, [newSettlement.super_agent_id, newSettlement.super_agent_name,
      newSettlement.agent_id, newSettlement.agent_name,
      newSettlement.player_id, newSettlement.player_name]);

  const canSubmit = validationErrors.length === 0
    && newSettlement.week && newSettlement.club_id && newSettlement.description;

  // Delete Confirmation Modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [settlementToDelete, setSettlementToDelete] = useState(null);

  // Summary
  const [summary, setSummary] = useState(null);

  // 載入週期列表（從 DB）
  const loadWeeks = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/data/weeks`);
      if (res.data.code === 1) {
        const dbWeeks = res.data.data.weeks || [];
        
        // 如果有週期，計算下一週並加入
        if (dbWeeks.length > 0) {
          const latestWeek = dbWeeks[0]; // 假設已經按新到舊排序
          const nextWeek = getNextWeek(latestWeek);
          const weeksWithNext = [nextWeek, ...dbWeeks];
          setAllWeeks(weeksWithNext);
          
          // 預設選擇最新週（下一週）
          if (!selectedWeek) {
            setSelectedWeek(nextWeek);
          }
        } else {
          setAllWeeks([]);
        }
      }
    } catch (err) {
      console.error("載入週列表失敗:", err);
    }
  }, [selectedWeek]);

  // 載入俱樂部列表
  const loadClubs = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/clubs`);
      console.log('載入俱樂部 API 回應:', res.data);
      if (res.data.code === 1 && res.data.data?.clubs) {
        console.log('載入的俱樂部:', res.data.data.clubs);
        setClubs(res.data.data.clubs);
      }
    } catch (err) {
      console.error("載入俱樂部列表失敗:", err);
    }
  }, []);

  // 載入額外交收列表
  const loadSettlements = useCallback(async () => {
    if (!selectedWeek) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append("week", selectedWeek);
      if (filterClub) params.append("club_id", filterClub);
      if (filterType) params.append("type", filterType);

      const res = await axios.get(`${API_BASE}/settlements/custom?${params}`);
      console.log('載入額外交收記錄 API 回應:', res.data);
      if (res.data.code === 1) {
        const loadedSettlements = res.data.data.settlements || [];
        console.log('載入的交收記錄:', loadedSettlements);
        setSettlements(loadedSettlements);
      } else {
        setError(res.data.msg);
      }
    } catch (err) {
      console.error('載入額外交收記錄錯誤:', err);
      setError("載入額外交收記錄失敗: " + (err.response?.data?.msg || err.message));
    } finally {
      setTimeout(() => setLoading(false), 100);
    }
  }, [selectedWeek, filterClub, filterType]);

  // 載入統計
  const loadSummary = useCallback(async () => {
    if (!selectedWeek) return;
    
    try {
      const res = await axios.get(`${API_BASE}/settlements/custom/summary/${selectedWeek}`);
      if (res.data.code === 1) {
        setSummary(res.data.data);
      }
    } catch (err) {
      console.error("載入統計失敗:", err);
    }
  }, [selectedWeek]);

  // 初始載入
  useEffect(() => {
    loadWeeks();
    loadClubs();
  }, [loadWeeks, loadClubs]);

  // 當週改變時載入資料
  useEffect(() => {
    if (selectedWeek) {
      loadSettlements();
      loadSummary();
    }
  }, [selectedWeek, filterClub, filterType, loadSettlements, loadSummary]);

  // 清除訊息
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // 開啟新增 Modal
  const openCreateModal = () => {
    setNewSettlement({
      ...DEFAULT_SETTLEMENT,
      week: selectedWeek,
    });
    setModalError(null);
    setModalOpen(true);
  };

  // 儲存額外交收（新增）
  const handleSave = async () => {
    setModalError(null);
    if (!canSubmit) return;

    setSaving(true);
    try {
      const res = await axios.post(`${API_BASE}/settlements/custom`, newSettlement);
      if (res.data.code === 1) {
        setSuccess("新增成功！");
        setModalOpen(false);
        loadSettlements();
        loadSummary();
      } else {
        setModalError(res.data.msg);
      }
    } catch (err) {
      setModalError("儲存失敗: " + (err.response?.data?.msg || err.message));
    } finally {
      setSaving(false);
    }
  };

  // 開啟刪除確認 Modal
  const openDeleteModal = (settlement) => {
    setSettlementToDelete(settlement);
    setDeleteModalOpen(true);
  };

  // 確認刪除
  const handleDelete = async () => {
    if (!settlementToDelete) return;
    
    try {
      const res = await axios.delete(`${API_BASE}/settlements/custom/${settlementToDelete._id}`);
      if (res.data.code === 1) {
        setSuccess("刪除成功！");
        setDeleteModalOpen(false);
        setSettlementToDelete(null);
        loadSettlements();
        loadSummary();
      } else {
        setError(res.data.msg);
      }
    } catch (err) {
      setError("刪除失敗: " + (err.response?.data?.msg || err.message));
    }
  };

  // 取得俱樂部名稱
  const getClubName = (clubId) => {
    const club = clubs.find(c => c.club_id === clubId);
    return club ? club.club_name : clubId;
  };

  // 取得類型配置
  const getTypeConfig = (type) => {
    return SETTLEMENT_TYPES.find(t => t.value === type) || SETTLEMENT_TYPES[5]; // 默認為 "其他"
  };

  // 格式化金額（從聯盟視角）
  const formatAmount = (amount) => {
    const num = parseFloat(amount) || 0;
    const formatted = Math.abs(num).toLocaleString();
    if (num < 0) {
      // 負數 = 俱樂部少拿 = 聯盟收入 = 綠色
      return <span style={{ color: "#2dce89", fontWeight: 600 }}>-${formatted}</span>;
    } else if (num > 0) {
      // 正數 = 俱樂部多拿 = 聯盟支出 = 紅色
      return <span style={{ color: "#f5365c", fontWeight: 600 }}>+${formatted}</span>;
    }
    return <span style={{ color: "rgba(255,255,255,0.8)" }}>$0</span>;
  };

  return (
    <div className="content">
      {/* 全局加載指示器：圈圈釘在畫面中間、舊資料留著 */}
      <TinyLoadingOverlay fixed active={loading} text="載入數據">

      {/* 提示訊息 */}
      {error && (
        <Alert 
          color="danger" 
          isOpen={!!error} 
          toggle={() => setError(null)}
          style={{ 
            background: 'rgba(245, 54, 92, 0.15)', 
            border: '1px solid rgba(245, 54, 92, 0.3)',
            color: '#fff'
          }}
        >
          <i className="fa fa-exclamation-circle mr-2" />
          {error}
        </Alert>
      )}
      {success && (
        <Alert 
          color="success" 
          isOpen={!!success}
          style={{ 
            background: 'rgba(45, 206, 137, 0.15)', 
            border: '1px solid rgba(45, 206, 137, 0.3)',
            color: '#fff'
          }}
        >
          <i className="fa fa-check-circle mr-2" />
          {success}
        </Alert>
      )}

      {/* 🔴 這裡原本有一張「額外交收管理」標題卡 —— 它跟外層
             TinySettlementCenter 的公版 header 是同一件事，一頁兩個標題。
             Brad 2026-09-10：「我們不需要兩個 header，對吧？」→ 已移除。
             這個元件只在 SettlementCenter 的「手動登記」tab 底下用，
             身分由外層 header + tab 表達就夠了。 */}
      {/* ⚠️ 統計原本是三張大 Card（總筆數 / 總金額 / 類型分佈）佔掉整整一排，
             把主表壓到摺線以下。Brad 2026-09-10：
             「映入眼簾的，應該是要以主 Table 為主才對啊。」
          → 兩個總計壓成一條小列常駐，佔比較大的「類型分佈」預設收起來。 */}
      {summary && (
        <div style={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 14,
          background: '#1e2530', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 8, padding: '9px 14px', marginBottom: 14,
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
            <i className="fa fa-list" style={{ color: '#667eea', fontSize: 13 }} />
            <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12 }}>總筆數</span>
            <span style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>{summary.total_count || 0}</span>
          </span>
          <span style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.12)' }} />
          <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
            <i className="fa fa-usd" style={{ color: (summary.total_amount || 0) >= 0 ? '#f5365c' : '#2dce89', fontSize: 13 }} />
            <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12 }}>總金額</span>
            <span style={{
              fontWeight: 700, fontSize: 16,
              color: (summary.total_amount || 0) >= 0 ? '#f5365c' : '#2dce89',
            }}>
              {(() => {
                const num = parseFloat(summary.total_amount) || 0;
                const formatted = Math.abs(num).toLocaleString();
                if (num < 0) return `-$${formatted}`;
                if (num > 0) return `+$${formatted}`;
                return '$0';
              })()}
            </span>
          </span>
          {Object.keys(summary.by_type || {}).length > 0 && (
            <>
              <span style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.12)' }} />
              <button
                onClick={() => setShowTypeBreakdown(v => !v)}
                style={{
                  background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
                  color: '#11cdef', fontSize: 12, fontWeight: 600,
                }}
              >
                <i className={`fa fa-chevron-${showTypeBreakdown ? 'up' : 'down'} mr-1`} style={{ fontSize: 10 }} />
                類型分佈（{Object.keys(summary.by_type).length} 類）
              </button>
            </>
          )}

          {showTypeBreakdown && (
            <div style={{ flexBasis: '100%', display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
              {Object.entries(summary.by_type || {}).map(([type, data]) => {
                const config = getTypeConfig(type);
                return (
                  <div key={type} style={{
                    background: config.bgColor,
                    border: `1px solid ${config.color}40`,
                    borderRadius: 6, padding: '5px 10px',
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                  }}>
                    <Badge style={{ background: config.color, fontSize: 10, padding: '3px 7px' }}>{config.label}</Badge>
                    <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>{data.count}筆</span>
                    <span style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>{formatAmount(data.amount)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* 主卡片 */}
      <Card style={{ background: '#1e2530', border: '1px solid rgba(255,255,255,0.1)' }}>
        <CardHeader style={{ background: '#252d3a', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <Row className="align-items-center">
            <Col md={6}>
              <CardTitle tag="h5" style={{ color: '#fff', marginBottom: 0, fontWeight: 600 }}>
                <i className="fa fa-list-ul mr-2" />
                額外交收記錄列表
              </CardTitle>
            </Col>
            <Col md={6} className="text-right">
              <Button 
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  padding: '10px 25px',
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                }}
                onClick={openCreateModal} 
                disabled={!selectedWeek}
              >
                <i className="fa fa-plus mr-2" />
                新增交收記錄
              </Button>
            </Col>
          </Row>
        </CardHeader>
        <CardBody style={{ padding: '20px' }}>
          {/* 篩選區 */}
          <Row className="mb-4">
            <Col md={4}>
              <TinyWeekSelector
                label={
                  <span style={{ color: "rgba(255,255,255,0.8)", fontWeight: 500, fontSize: "13px" }}>
                    <i className="fa fa-calendar mr-2" />
                    週期
                  </span>
                }
                options={allWeeks}
                value={selectedWeek}
                onChange={setSelectedWeek}
                includeEmptyOption
                markFirstAsNextWeek
              />
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label style={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500, fontSize: '13px' }}>
                  <i className="fa fa-building mr-2" />
                  俱樂部
                </Label>
                <Input
                  type="select"
                  value={filterClub}
                  onChange={(e) => setFilterClub(e.target.value)}
                  style={{
                    background: '#1a1f2b',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '6px'
                  }}
                >
                  <option value="">全部俱樂部</option>
                  {clubs.map((club) => (
                    <option key={club.club_id} value={club.club_id}>
                      {club.club_name} ({club.club_id})
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label style={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500, fontSize: '13px' }}>
                  <i className="fa fa-tag mr-2" />
                  類型
                </Label>
                <Input
                  type="select"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  style={{
                    background: '#1a1f2b',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '6px'
                  }}
                >
                  <option value="">全部類型</option>
                  {SETTLEMENT_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>

          {/* 表格 */}
          {!selectedWeek ? (
            <div className="text-center p-5">
              <i className="fa fa-calendar" style={{ fontSize: '4rem', color: 'rgba(255,255,255,0.2)', marginBottom: '20px' }} />
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '1.1rem' }}>請選擇週期以查看額外交收記錄</p>
            </div>
          ) : settlements.length === 0 && !loading ? (
            <div className="text-center p-5">
              <i className="fa fa-inbox" style={{ fontSize: '4rem', color: 'rgba(255,255,255,0.2)', marginBottom: '20px' }} />
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '1.1rem', marginBottom: '10px' }}>此週尚無額外交收記錄</p>
              <Button 
                color="primary" 
                size="sm" 
                onClick={openCreateModal}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  padding: '8px 20px'
                }}
              >
                <i className="fa fa-plus mr-2" />
                新增第一筆記錄
              </Button>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <Table responsive hover size="sm" style={{ marginBottom: 0, color: 'rgba(255,255,255,0.8)' }}>
                <thead style={{ background: '#252d3a', color: '#fff' }}>
                  <tr>
                    <th style={{ borderColor: 'rgba(255,255,255,0.1)', width: '40px' }}>#</th>
                    <th style={{ borderColor: 'rgba(255,255,255,0.1)' }}>俱樂部</th>
                    <th style={{ borderColor: 'rgba(255,255,255,0.1)' }}>超級代理</th>
                    <th style={{ borderColor: 'rgba(255,255,255,0.1)' }}>代理</th>
                    <th style={{ borderColor: 'rgba(255,255,255,0.1)' }}>玩家</th>
                    <th style={{ borderColor: 'rgba(255,255,255,0.1)', width: '100px' }}>類型</th>
                    <th style={{ borderColor: 'rgba(255,255,255,0.1)', textAlign: 'right', width: '110px' }}>金額</th>
                    <th style={{ borderColor: 'rgba(255,255,255,0.1)' }}>說明</th>
                    <th style={{ borderColor: 'rgba(255,255,255,0.1)', textAlign: 'center', whiteSpace: 'nowrap' }}>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {settlements.map((s, idx) => {
                    const typeConfig = getTypeConfig(s.type);
                    const clubName = getClubName(s.club_id);
                    return (
                      <tr key={s._id || idx} style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                        <td style={{ borderColor: 'rgba(255,255,255,0.05)', verticalAlign: 'middle' }}>{idx + 1}</td>
                        <td style={{ borderColor: 'rgba(255,255,255,0.05)', verticalAlign: 'middle' }}>
                          <div style={{ fontWeight: 500 }}>{clubName || '未知俱樂部'}</div>
                          <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', fontFamily: 'monospace' }}>{s.club_id || '-'}</span>
                        </td>
                        <td style={{ borderColor: 'rgba(255,255,255,0.05)', verticalAlign: 'middle' }}>
                          {s.super_agent_id ? (
                            <div>
                              <div style={{ fontWeight: 500, color: '#11cdef', fontSize: '13px' }}>{s.super_agent_name || '-'}</div>
                              <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', fontFamily: 'monospace' }}>{s.super_agent_id}</span>
                            </div>
                          ) : <span style={{ color: 'rgba(255,255,255,0.25)' }}>-</span>}
                        </td>
                        <td style={{ borderColor: 'rgba(255,255,255,0.05)', verticalAlign: 'middle' }}>
                          {s.agent_id ? (
                            <div>
                              <div style={{ fontWeight: 500, color: '#fb6340', fontSize: '13px' }}>{s.agent_name || '-'}</div>
                              <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', fontFamily: 'monospace' }}>{s.agent_id}</span>
                            </div>
                          ) : <span style={{ color: 'rgba(255,255,255,0.25)' }}>-</span>}
                        </td>
                        <td style={{ borderColor: 'rgba(255,255,255,0.05)', verticalAlign: 'middle' }}>
                          {s.player_id ? (
                            <div>
                              <div style={{ fontWeight: 500, color: '#ffd600', fontSize: '13px' }}>{s.player_name || '-'}</div>
                              <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', fontFamily: 'monospace' }}>{s.player_id}</span>
                            </div>
                          ) : <span style={{ color: 'rgba(255,255,255,0.25)' }}>-</span>}
                        </td>
                        <td style={{ borderColor: 'rgba(255,255,255,0.05)', verticalAlign: 'middle' }}>
                          <Badge 
                            style={{ 
                              background: typeConfig.color,
                              fontSize: '11px',
                              padding: '5px 10px'
                            }}
                          >
                            {typeConfig.label}
                          </Badge>
                        </td>
                        <td style={{ borderColor: 'rgba(255,255,255,0.05)', textAlign: 'right', fontSize: '14px', verticalAlign: 'middle' }}>
                          {formatAmount(s.amount)}
                        </td>
                        <td style={{ borderColor: 'rgba(255,255,255,0.05)', verticalAlign: 'middle' }}>
                          <div style={{ maxWidth: '300px' }}>
                            <div style={{ marginBottom: '2px' }}>{s.description || '-'}</div>
                            {s.reason && (
                              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
                                <i className="fa fa-info-circle mr-1" />
                                {s.reason}
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ borderColor: 'rgba(255,255,255,0.05)', textAlign: 'center', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          <Button
                            size="sm"
                            onClick={() => openDeleteModal(s)}
                            style={{
                              background: 'rgba(245, 54, 92, 0.2)',
                              border: '1px solid rgba(245, 54, 92, 0.5)',
                              color: '#f5365c',
                              padding: '4px 10px',
                              borderRadius: '4px'
                            }}
                          >
                            <i className="fa fa-trash mr-1" />刪除
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* 新增 Modal */}
      <Modal 
        isOpen={modalOpen} 
        toggle={() => setModalOpen(false)} 
        size="lg"
        style={{ marginTop: '50px' }}
      >
        <ModalHeader 
          toggle={() => setModalOpen(false)}
          style={{ 
            background: '#252d3a', 
            color: '#fff',
            borderBottom: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <i className="fa fa-plus-circle mr-2" style={{ color: '#667eea' }} />
          新增額外交收記錄
        </ModalHeader>
        <ModalBody style={{ background: '#1e2530', color: '#fff' }}>
          {modalError && (
            <Alert
              color="danger"
              isOpen={!!modalError}
              toggle={() => setModalError(null)}
              style={{
                background: 'rgba(245, 54, 92, 0.15)',
                border: '1px solid rgba(245, 54, 92, 0.3)',
                color: '#fff',
                marginBottom: '15px'
              }}
            >
              <i className="fa fa-exclamation-circle mr-2" />
              {modalError}
            </Alert>
          )}
          {/* 基本資訊 */}
          <div style={{ 
            background: 'rgba(102, 126, 234, 0.1)', 
            border: '1px solid rgba(102, 126, 234, 0.3)',
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '20px'
          }}>
            <h6 style={{ color: '#667eea', marginBottom: '15px', fontSize: '14px', fontWeight: 600 }}>
              <i className="fa fa-info-circle mr-2" />
              基本資訊
            </h6>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>
                    週期 <span style={{ color: '#f5365c' }}>*</span>
                  </Label>
                  <Input
                    type="select"
                    value={newSettlement.week}
                    onChange={(e) => setNewSettlement({ ...newSettlement, week: e.target.value })}
                    style={{
                      background: '#1a1f2b',
                      color: '#fff',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '6px'
                    }}
                  >
                    <option value="">選擇週期</option>
                    {allWeeks.map((week, idx) => (
                      <option key={week} value={week}>
                        {week} {idx === 0 ? '(下一週)' : ''}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>
                    俱樂部 <span style={{ color: '#f5365c' }}>*</span>
                  </Label>
                  <Input
                    type="select"
                    value={newSettlement.club_id}
                    onChange={(e) => setNewSettlement({ ...newSettlement, club_id: e.target.value })}
                    style={{
                      background: '#1a1f2b',
                      color: '#fff',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '6px'
                    }}
                  >
                    <option value="">選擇俱樂部</option>
                    {clubs.map((club) => (
                      <option key={club.club_id} value={club.club_id}>
                        {club.club_name} ({club.club_id})
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>
          </div>

          {/* 交收對象 */}
          <div style={{ 
            background: 'rgba(45, 206, 137, 0.1)', 
            border: '1px solid rgba(45, 206, 137, 0.3)',
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '20px'
          }}>
            <h6 style={{ color: '#2dce89', marginBottom: '15px', fontSize: '14px', fontWeight: 600 }}>
              <i className="fa fa-users mr-2" />
              交收對象
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 400, marginLeft: '8px' }}>
                （皆可留空 — 三層全空表示對俱樂部本身做雜項調整）
              </span>
            </h6>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>超級代理 ID</Label>
                  <Input
                    type="text"
                    placeholder="如：7899-4237"
                    value={newSettlement.super_agent_id || ""}
                    onChange={(e) => setNewSettlement({ ...newSettlement, super_agent_id: e.target.value })}
                    style={{
                      background: '#1a1f2b',
                      color: '#fff',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '6px'
                    }}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>超級代理名稱</Label>
                  <Input
                    type="text"
                    placeholder="如：jrscout"
                    value={newSettlement.super_agent_name || ""}
                    onChange={(e) => setNewSettlement({ ...newSettlement, super_agent_name: e.target.value })}
                    style={{
                      background: '#1a1f2b',
                      color: '#fff',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '6px'
                    }}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>代理 ID</Label>
                  <Input
                    type="text"
                    placeholder="如：1234-5678"
                    value={newSettlement.agent_id || ""}
                    onChange={(e) => setNewSettlement({ ...newSettlement, agent_id: e.target.value })}
                    style={{
                      background: '#1a1f2b',
                      color: '#fff',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '6px'
                    }}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>代理名稱</Label>
                  <Input
                    type="text"
                    placeholder="如：AgentName"
                    value={newSettlement.agent_name || ""}
                    onChange={(e) => setNewSettlement({ ...newSettlement, agent_name: e.target.value })}
                    style={{
                      background: '#1a1f2b',
                      color: '#fff',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '6px'
                    }}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>玩家 ID</Label>
                  <Input
                    type="text"
                    placeholder="如：2890-2951"
                    value={newSettlement.player_id || ""}
                    onChange={(e) => setNewSettlement({ ...newSettlement, player_id: e.target.value })}
                    style={{
                      background: '#1a1f2b',
                      color: '#fff',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '6px'
                    }}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>玩家名稱</Label>
                  <Input
                    type="text"
                    placeholder="如：PlayerName"
                    value={newSettlement.player_name || ""}
                    onChange={(e) => setNewSettlement({ ...newSettlement, player_name: e.target.value })}
                    style={{
                      background: '#1a1f2b',
                      color: '#fff',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '6px'
                    }}
                  />
                </FormGroup>
              </Col>
            </Row>
            {validationErrors.length > 0 && (
              <div style={{
                background: 'rgba(245, 54, 92, 0.1)',
                border: '1px solid rgba(245, 54, 92, 0.25)',
                borderRadius: '6px',
                padding: '10px 14px',
                marginTop: '5px'
              }}>
                {validationErrors.map((err, i) => (
                  <div key={i} style={{ color: '#f5365c', fontSize: '12px', lineHeight: '20px' }}>
                    <i className="fa fa-times-circle mr-1" style={{ fontSize: '11px' }} />
                    {err}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 交收詳情 */}
          <div style={{ 
            background: 'rgba(251, 99, 64, 0.1)', 
            border: '1px solid rgba(251, 99, 64, 0.3)',
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '20px'
          }}>
            <h6 style={{ color: '#fb6340', marginBottom: '15px', fontSize: '14px', fontWeight: 600 }}>
              <i className="fa fa-file-text mr-2" />
              交收詳情
            </h6>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>
                    類型 <span style={{ color: '#f5365c' }}>*</span>
                  </Label>
                  <Input
                    type="select"
                    value={newSettlement.type}
                    onChange={(e) => setNewSettlement({ ...newSettlement, type: e.target.value })}
                    style={{
                      background: '#1a1f2b',
                      color: '#fff',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '6px'
                    }}
                  >
                    {SETTLEMENT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>
                    金額 <span style={{ color: '#f5365c' }}>*</span>
                  </Label>
                  <InputGroup>
                    <InputGroupText style={{ background: '#1a1f2b', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}>
                      $
                    </InputGroupText>
                    <Input
                      type="number"
                      value={newSettlement.amount}
                      onChange={(e) => setNewSettlement({ ...newSettlement, amount: parseFloat(e.target.value) || 0 })}
                      style={{
                        background: '#1a1f2b',
                        color: '#fff',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '0 6px 6px 0'
                      }}
                    />
                  </InputGroup>
                  <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', marginTop: '5px', display: 'block' }}>
                    負數 = 向俱樂部收費（聯盟收入）｜正數 = 給俱樂部錢（聯盟支出）
                  </small>
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              <Label style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>
                說明 <span style={{ color: '#f5365c' }}>*</span>
              </Label>
              <Input
                type="text"
                placeholder="簡短說明"
                value={newSettlement.description}
                onChange={(e) => setNewSettlement({ ...newSettlement, description: e.target.value })}
                style={{
                  background: '#1a1f2b',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '6px'
                }}
              />
            </FormGroup>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>詳細原因（選填）</Label>
              <Input
                type="textarea"
                rows={3}
                placeholder="詳細原因說明"
                value={newSettlement.reason || ""}
                onChange={(e) => setNewSettlement({ ...newSettlement, reason: e.target.value })}
                style={{
                  background: '#1a1f2b',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '6px'
                }}
              />
            </FormGroup>
          </div>
        </ModalBody>
        <ModalFooter style={{ background: '#252d3a', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Button 
            onClick={() => setModalOpen(false)}
            style={{
              background: 'rgba(149, 165, 166, 0.2)',
              border: '1px solid rgba(149, 165, 166, 0.5)',
              color: '#95a5a6',
              padding: '8px 20px',
              borderRadius: '6px'
            }}
          >
            取消
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving || !canSubmit}
            style={{
              background: (saving || !canSubmit) ? 'rgba(102, 126, 234, 0.3)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              padding: '8px 30px',
              borderRadius: '6px',
              fontWeight: 600,
              boxShadow: (saving || !canSubmit) ? 'none' : '0 4px 12px rgba(102, 126, 234, 0.3)',
              cursor: (saving || !canSubmit) ? 'not-allowed' : 'pointer',
              opacity: (saving || !canSubmit) ? 0.6 : 1
            }}
          >
            {saving ? (
              <>
                <TinyBusy light style={{ marginRight: 6 }} />
                儲存中
              </>
            ) : (
              <>
                <i className="fa fa-check mr-2" />
                儲存
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* 刪除確認 Modal */}
      <Modal 
        isOpen={deleteModalOpen} 
        toggle={() => setDeleteModalOpen(false)}
        style={{ marginTop: '100px' }}
      >
        <ModalHeader 
          toggle={() => setDeleteModalOpen(false)}
          style={{ 
            background: '#252d3a', 
            color: '#fff',
            borderBottom: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <i className="fa fa-exclamation-triangle mr-2" style={{ color: '#f5365c' }} />
          確認刪除
        </ModalHeader>
        <ModalBody style={{ background: '#1e2530', color: '#fff' }}>
          {settlementToDelete && (
            <div>
              <div style={{ 
                background: 'rgba(245, 54, 92, 0.1)', 
                border: '1px solid rgba(245, 54, 92, 0.3)',
                borderRadius: '8px',
                padding: '15px',
                marginBottom: '15px'
              }}>
                <p style={{ marginBottom: '10px', color: 'rgba(255,255,255,0.9)' }}>
                  確定要刪除此額外交收記錄嗎？此操作無法復原。
                </p>
              </div>
              <div style={{ 
                background: 'rgba(255,255,255,0.05)', 
                borderRadius: '8px',
                padding: '15px'
              }}>
                <div style={{ marginBottom: '10px' }}>
                  <strong style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>俱樂部：</strong>
                  <span style={{ marginLeft: '10px' }}>{getClubName(settlementToDelete.club_id)}</span>
                </div>
                {settlementToDelete.super_agent_id && (
                  <div style={{ marginBottom: '10px' }}>
                    <strong style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>超代：</strong>
                    <span style={{ marginLeft: '10px' }}>{settlementToDelete.super_agent_name || settlementToDelete.super_agent_id}</span>
                  </div>
                )}
                {settlementToDelete.agent_id && (
                  <div style={{ marginBottom: '10px' }}>
                    <strong style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>代理：</strong>
                    <span style={{ marginLeft: '10px' }}>{settlementToDelete.agent_name || settlementToDelete.agent_id}</span>
                  </div>
                )}
                {settlementToDelete.player_id && (
                  <div style={{ marginBottom: '10px' }}>
                    <strong style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>玩家：</strong>
                    <span style={{ marginLeft: '10px' }}>{settlementToDelete.player_name || settlementToDelete.player_id}</span>
                  </div>
                )}
                <div style={{ marginBottom: '10px' }}>
                  <strong style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>類型：</strong>
                  <Badge 
                    style={{ 
                      background: getTypeConfig(settlementToDelete.type).color,
                      marginLeft: '10px',
                      padding: '5px 10px'
                    }}
                  >
                    {getTypeConfig(settlementToDelete.type).label}
                  </Badge>
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <strong style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>說明：</strong>
                  <span style={{ marginLeft: '10px' }}>{settlementToDelete.description}</span>
                </div>
                <div>
                  <strong style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>金額：</strong>
                  <span style={{ marginLeft: '10px', fontSize: '16px' }}>{formatAmount(settlementToDelete.amount)}</span>
                </div>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter style={{ background: '#252d3a', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Button 
            onClick={() => setDeleteModalOpen(false)}
            style={{
              background: 'rgba(149, 165, 166, 0.2)',
              border: '1px solid rgba(149, 165, 166, 0.5)',
              color: '#95a5a6',
              padding: '8px 20px',
              borderRadius: '6px'
            }}
          >
            取消
          </Button>
          <Button 
            onClick={handleDelete}
            style={{
              background: 'linear-gradient(135deg, #f5365c 0%, #f56036 100%)',
              border: 'none',
              padding: '8px 30px',
              borderRadius: '6px',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(245, 54, 92, 0.3)'
            }}
          >
            <i className="fa fa-trash mr-2" />
            確認刪除
          </Button>
        </ModalFooter>
      </Modal>
      </TinyLoadingOverlay>
    </div>
  );
}

export default TinyCustomSettlements;


